<script setup>
import { ref, computed, watch, toRef } from 'vue'
import { useBackButtonClose } from '../../../../core/composables/useBackButtonClose.js'
import { setAuthDisabled, setUserRole, updateUserProfile } from '@core/services/users.js'
import { normalizePhoneNumber } from '@core/utils/formatting.js'
import { getUserStatusLabel } from '@core/utils/statusHelpers.js'
import ClientLinkModal from '../ClientLinkModal.vue'
import ImageUpload from '../../../components/common/ImageUpload.vue'
import { useStorage } from '@core/composables/useStorage.js'

const props = defineProps({
  open: { type: Boolean, default: false },
  user: { type: Object, default: null },
  authUsers: { type: Array, required: true },
})

const emit = defineEmits(['close', 'updated', 'openDelete'])

// ============================================
// Local state
// ============================================
const drawerLoading = ref(false)
const drawerError = ref(null)
const drawerSuccess = ref(null)
const editForm = ref({ name: '', phone: '', status: 'new', client_makat: '' })
const editRole = ref(null)
const agentNumberDraft = ref('')
const agentNumberError = ref('')

// Avatar editing state
const isEditingAvatar = ref(false)
const newAvatarUrl = ref('')
const savingAvatar = ref(false)
const avatarError = ref(null)
const { deleteImageByUrl } = useStorage()

// Client link modal
const isClientLinkModalOpen = ref(false)
const isSubClientLinkModalOpen = ref(false)

// Status / role options
const statusOptions = ['new', 'active', 'inactive', 'deleted']
const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'client', label: 'Client' },
  { value: 'agent', label: 'Agent' },
  { value: null, label: 'None' },
]

// Local copy of the user so we can mutate client link etc.
const localUser = ref(null)

const currentAvatarUrl = computed(() => localUser.value?.profile?.photoURL || null)

const subClients = computed(() => {
  const arr = localUser.value?.profile?.sub_clients
  return Array.isArray(arr) ? arr : []
})
const subClientMakats = computed(() => subClients.value.map((sc) => String(sc.makat)))
const subClientExcludedMakats = computed(() => {
  const list = [...subClientMakats.value]
  const primary = localUser.value?.profile?.client_makat
  if (primary) list.push(String(primary))
  return list
})

const avatarInitial = computed(() => {
  return (
    localUser.value?.profile?.name ||
    localUser.value?.displayName ||
    localUser.value?.email ||
    '?'
  )
    .charAt(0)
    .toUpperCase()
})

// ============================================
// Watch props.open to populate form
// ============================================
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen && props.user) {
      localUser.value = JSON.parse(JSON.stringify(props.user))
      const profile = props.user.profile || {}
      editForm.value = {
        name: profile.name || '',
        phone: profile.phone || '',
        status: profile.status || 'new',
        client_makat: profile.client_makat || '',
      }
      editRole.value = props.user.role
      agentNumberDraft.value = profile.agent != null ? String(profile.agent) : ''
      agentNumberError.value = ''
      drawerError.value = null
      drawerSuccess.value = null
      drawerLoading.value = false
      isEditingAvatar.value = false
      newAvatarUrl.value = ''
      avatarError.value = null
    }
  }
)

// Auto-remove leading '0' in edit phone
watch(
  () => editForm.value.phone,
  (newPhone) => {
    if (!newPhone) return
    let cleaned = newPhone.replace(/[\s-]/g, '')
    if (cleaned.startsWith('0')) {
      editForm.value.phone = cleaned.substring(1)
    }
  }
)

// ============================================
// Agent Number Validation
// ============================================
function validateAgentNumber(value) {
  if (value === '') return ''
  const num = Number(value)
  if (isNaN(num) || !Number.isInteger(num)) return 'יש להזין מספר שלם'
  if (num < 0) return 'מספר הסוכן חייב להיות חיובי'
  if (num > 999) return 'מספר הסוכן לא יכול להיות גדול מ-999'
  return ''
}

function handleAgentNumberInput(event) {
  let value = event.target.value.replace(/[^0-9]/g, '')
  if (value.length > 3) value = value.slice(0, 3)
  agentNumberDraft.value = value
  agentNumberError.value = validateAgentNumber(value)
}

function handleAgentNumberKeydown(event) {
  const allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ]
  if (allowedKeys.includes(event.key)) return
  if (event.ctrlKey || event.metaKey) return
  if (!/^[0-9]$/.test(event.key)) event.preventDefault()
}

const canSaveProfile = computed(() => {
  if (editRole.value !== 'agent') return true
  return agentNumberError.value === ''
})

// ============================================
// Avatar Editing
// ============================================
function startEditingAvatar() {
  newAvatarUrl.value = localUser.value?.profile?.photoURL || ''
  avatarError.value = null
  isEditingAvatar.value = true
}

function cancelEditingAvatar() {
  isEditingAvatar.value = false
  newAvatarUrl.value = ''
  avatarError.value = null
}

async function saveAvatar() {
  if (!localUser.value) return
  savingAvatar.value = true
  avatarError.value = null

  try {
    const oldUrl = localUser.value?.profile?.photoURL
    if (oldUrl && oldUrl !== newAvatarUrl.value) {
      try {
        await deleteImageByUrl(oldUrl)
      } catch (err) {
        console.warn('Could not delete old avatar:', err)
      }
    }

    const newPhotoURL = newAvatarUrl.value || null
    await updateUserProfile(localUser.value.uid, { photoURL: newPhotoURL })

    if (!localUser.value.profile) localUser.value.profile = {}
    localUser.value.profile.photoURL = newPhotoURL

    isEditingAvatar.value = false
    newAvatarUrl.value = ''
    drawerSuccess.value = 'התמונה נשמרה בהצלחה'
    emit('updated')
  } catch (err) {
    console.error('Failed to save avatar:', err)
    avatarError.value = 'שגיאה בשמירת התמונה'
  } finally {
    savingAvatar.value = false
  }
}

async function removeAvatar() {
  if (!localUser.value) return
  savingAvatar.value = true

  try {
    const oldUrl = localUser.value?.profile?.photoURL
    if (oldUrl) {
      try {
        await deleteImageByUrl(oldUrl)
      } catch (err) {
        console.warn('Could not delete avatar:', err)
      }
    }

    await updateUserProfile(localUser.value.uid, { photoURL: null })

    if (!localUser.value.profile) localUser.value.profile = {}
    localUser.value.profile.photoURL = null

    drawerSuccess.value = 'התמונה הוסרה בהצלחה'
    emit('updated')
  } catch (err) {
    console.error('Failed to remove avatar:', err)
    drawerError.value = 'שגיאה בהסרת התמונה'
  } finally {
    savingAvatar.value = false
  }
}

// ============================================
// Actions
// ============================================
function close() {
  emit('close')
}

// Intercept back button to close drawer instead of navigating
useBackButtonClose(toRef(props, 'open'), close)

async function saveProfile() {
  if (!localUser.value) return
  if (editRole.value === 'agent' && agentNumberError.value) {
    drawerError.value = agentNumberError.value
    return
  }

  drawerLoading.value = true
  drawerError.value = null
  drawerSuccess.value = null

  try {
    const profileData = {
      name: editForm.value.name || null,
      phone: editForm.value.phone ? normalizePhoneNumber(editForm.value.phone) : null,
      status: editForm.value.status || 'new',
      client_makat: editForm.value.client_makat || null,
    }
    if (editRole.value === 'agent') {
      profileData.agent =
        agentNumberDraft.value === '' ? null : String(Number(agentNumberDraft.value))
    }
    await updateUserProfile(localUser.value.uid, profileData)
    drawerSuccess.value = 'הפרופיל נשמר בהצלחה'
    emit('updated')
  } catch (err) {
    console.error('Failed to save profile:', err)
    drawerError.value = err.message || 'שגיאה בשמירת הפרופיל'
  } finally {
    drawerLoading.value = false
  }
}

async function saveRole() {
  if (!localUser.value) return
  drawerLoading.value = true
  drawerError.value = null
  drawerSuccess.value = null

  try {
    await setUserRole(localUser.value.uid, editRole.value)
    // Update local authUsers array
    const idx = props.authUsers.findIndex((u) => u.uid === localUser.value.uid)
    if (idx !== -1) {
      props.authUsers[idx] = { ...props.authUsers[idx], role: editRole.value }
    }
    drawerSuccess.value = 'התפקיד עודכן בהצלחה'
    emit('updated')
  } catch (err) {
    console.error('Failed to save role:', err)
    drawerError.value = err.message || 'שגיאה בעדכון התפקיד'
  } finally {
    drawerLoading.value = false
  }
}

async function toggleDrawerDisabled() {
  if (!localUser.value) return
  drawerLoading.value = true
  drawerError.value = null
  drawerSuccess.value = null
  const newDisabled = !localUser.value.disabled

  try {
    await setAuthDisabled(localUser.value.uid, newDisabled)
    const idx = props.authUsers.findIndex((u) => u.uid === localUser.value.uid)
    if (idx !== -1) {
      props.authUsers[idx] = { ...props.authUsers[idx], disabled: newDisabled }
    }
    localUser.value = { ...localUser.value, disabled: newDisabled }
    drawerSuccess.value = newDisabled ? 'המשתמש הושבת' : 'המשתמש הופעל'
    emit('updated')
  } catch (err) {
    console.error('Failed to toggle disabled:', err)
    drawerError.value = err.message || 'שגיאה בעדכון הסטטוס'
  } finally {
    drawerLoading.value = false
  }
}

// ============================================
// Client Link
// ============================================
function handleClientLinked(payload) {
  if (localUser.value) {
    if (!localUser.value.profile) localUser.value.profile = {}
    localUser.value.profile.client_makat = payload.client_makat
    localUser.value.profile.client_name = payload.client_name
    editForm.value.client_makat = payload.client_makat
  }
  drawerSuccess.value = `שויך ללקוח ${payload.client_makat} — ${payload.client_name}`
  emit('updated')
}

async function releaseClientLink() {
  if (!localUser.value) return
  drawerLoading.value = true
  drawerError.value = null
  drawerSuccess.value = null

  try {
    await updateUserProfile(localUser.value.uid, { client_makat: null, client_name: null })
    if (localUser.value.profile) {
      localUser.value.profile.client_makat = null
      localUser.value.profile.client_name = null
    }
    editForm.value.client_makat = ''
    drawerSuccess.value = 'הלקוח שוחרר בהצלחה'
    emit('updated')
  } catch (err) {
    console.error('Failed to release client link:', err)
    drawerError.value = err.message || 'שגיאה בשחרור הלקוח'
  } finally {
    drawerLoading.value = false
  }
}

// ============================================
// Sub-Clients
// ============================================
async function handleSubClientLinked(payload) {
  if (!localUser.value) return
  const makat = String(payload.makat || payload.client_makat || '')
  const name = String(payload.name || payload.client_name || '')
  if (!makat) return

  const current = Array.isArray(localUser.value?.profile?.sub_clients)
    ? localUser.value.profile.sub_clients
    : []
  if (current.some((sc) => String(sc.makat) === makat)) {
    drawerSuccess.value = `תת-הלקוח ${makat} כבר משויך`
    return
  }
  const next = [...current, { makat, name }]

  drawerLoading.value = true
  drawerError.value = null
  drawerSuccess.value = null

  try {
    await updateUserProfile(localUser.value.uid, { sub_clients: next })
    if (!localUser.value.profile) localUser.value.profile = {}
    localUser.value.profile.sub_clients = next
    drawerSuccess.value = `תת-לקוח ${makat} — ${name || 'ללא שם'} שויך בהצלחה`
    emit('updated')
  } catch (err) {
    console.error('Failed to add sub-client:', err)
    drawerError.value = err.message || 'שגיאה בשיוך תת-הלקוח'
  } finally {
    drawerLoading.value = false
  }
}

async function releaseSubClient(makat) {
  if (!localUser.value) return
  const target = String(makat)
  const current = Array.isArray(localUser.value?.profile?.sub_clients)
    ? localUser.value.profile.sub_clients
    : []
  const next = current.filter((sc) => String(sc.makat) !== target)
  if (next.length === current.length) return

  drawerLoading.value = true
  drawerError.value = null
  drawerSuccess.value = null

  try {
    await updateUserProfile(localUser.value.uid, { sub_clients: next })
    if (!localUser.value.profile) localUser.value.profile = {}
    localUser.value.profile.sub_clients = next
    drawerSuccess.value = `תת-לקוח ${target} שוחרר`
    emit('updated')
  } catch (err) {
    console.error('Failed to release sub-client:', err)
    drawerError.value = err.message || 'שגיאה בשחרור תת-הלקוח'
  } finally {
    drawerLoading.value = false
  }
}

function openDeleteFromDrawer() {
  emit('openDelete', localUser.value)
}
</script>

<template>
  <Transition name="drawer">
    <div v-if="open" class="drawer-overlay" @click.self="close">
      <div class="drawer">
        <div class="drawer-header">
          <h2 class="drawer-title">עריכת משתמש</h2>
          <button class="drawer-close" @click="close">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="drawer-body">
          <!-- User info header -->
          <div class="user-header">
            <div class="user-avatar-container">
              <template v-if="!isEditingAvatar">
                <img
                  v-if="currentAvatarUrl"
                  :src="currentAvatarUrl"
                  alt=""
                  class="user-avatar-img"
                />
                <div v-else class="user-avatar">{{ avatarInitial }}</div>
                <button class="avatar-edit-btn" @click="startEditingAvatar" title="עריכת תמונה">
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"
                    />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                </button>
              </template>
            </div>
            <div class="user-meta">
              <span class="user-email">{{ localUser?.email }}</span>
              <span class="user-uid">{{ localUser?.uid }}</span>
              <button
                v-if="currentAvatarUrl && !isEditingAvatar"
                class="btn-remove-avatar"
                :disabled="savingAvatar"
                @click="removeAvatar"
              >
                הסר תמונה
              </button>
            </div>
          </div>

          <!-- Avatar edit mode -->
          <div v-if="isEditingAvatar" class="avatar-edit-section">
            <ImageUpload
              v-model="newAvatarUrl"
              folder="user-avatars"
              :file-name="localUser?.uid || ''"
              label=""
            />
            <div class="avatar-edit-actions">
              <button
                class="btn-avatar-cancel"
                :disabled="savingAvatar"
                @click="cancelEditingAvatar"
              >
                ביטול
              </button>
              <button class="btn-avatar-save" :disabled="savingAvatar" @click="saveAvatar">
                {{ savingAvatar ? 'שומר...' : 'שמור תמונה' }}
              </button>
            </div>
            <p v-if="avatarError" class="avatar-error">{{ avatarError }}</p>
          </div>

          <!-- Messages -->
          <div v-if="drawerError" class="msg msg-error">{{ drawerError }}</div>
          <div v-if="drawerSuccess" class="msg msg-success">{{ drawerSuccess }}</div>

          <!-- Profile Section -->
          <div class="drawer-section">
            <h3 class="section-title">פרופיל</h3>
            <div class="form-group">
              <label class="form-label">שם</label>
              <input v-model="editForm.name" type="text" class="form-input" placeholder="שם מלא" />
            </div>
            <div class="form-group">
              <label class="form-label">טלפון</label>
              <p class="form-hint">* ללא ה-0 הראשון במספר</p>
              <div class="phone-input-wrapper">
                <input
                  v-model="editForm.phone"
                  type="text"
                  class="form-input phone-input-with-prefix"
                  placeholder="544221254"
                />
                <span class="phone-prefix">+972</span>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">סטטוס</label>
              <select v-model="editForm.status" class="form-input">
                <option v-for="opt in statusOptions" :key="opt" :value="opt">
                  {{ getUserStatusLabel(opt) }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">מק״ט לקוח (client_makat)</label>
              <input
                v-model="editForm.client_makat"
                type="text"
                class="form-input"
                placeholder="CUSTNAME מ-ClientsV2"
              />
            </div>

            <!-- Agent Number -->
            <div v-if="editRole === 'agent'" class="form-group">
              <label class="form-label">מספר סוכן (0–999)</label>
              <input
                :value="agentNumberDraft"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                class="form-input"
                :class="{ 'input-error': agentNumberError }"
                placeholder="הזן מספר סוכן"
                maxlength="3"
                @input="handleAgentNumberInput"
                @keydown="handleAgentNumberKeydown"
              />
              <span v-if="agentNumberError" class="input-error-text">{{ agentNumberError }}</span>
            </div>

            <button
              class="btn-save"
              :disabled="drawerLoading || !canSaveProfile"
              @click="saveProfile"
            >
              {{ drawerLoading ? 'שומר...' : 'שמור פרופיל' }}
            </button>
          </div>

          <!-- Role Section -->
          <div class="drawer-section">
            <h3 class="section-title">תפקיד (Claims)</h3>
            <div class="form-group">
              <label class="form-label">תפקיד</label>
              <select v-model="editRole" class="form-input">
                <option v-for="opt in roleOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <button class="btn-save" :disabled="drawerLoading" @click="saveRole">
              {{ drawerLoading ? 'שומר...' : 'שמור תפקיד' }}
            </button>
          </div>

          <!-- Client Link Section -->
          <div class="drawer-section">
            <h3 class="section-title">שיוך לקוחות</h3>
            <div class="client-link-display">
              <span class="client-link-label">לקוח משויך:</span>
              <span v-if="localUser?.profile?.client_makat" class="client-link-value linked">
                {{ localUser.profile.client_makat }} —
                {{ localUser.profile.client_name || 'ללא שם' }}
              </span>
              <span v-else class="client-link-value not-linked">לא משויך</span>
            </div>
            <div class="client-link-buttons">
              <button
                class="btn-link-client"
                :disabled="drawerLoading"
                @click="isClientLinkModalOpen = true"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                </svg>
                <span>שייך</span>
              </button>
              <button
                v-if="localUser?.profile?.client_makat"
                class="btn-release-client"
                :disabled="drawerLoading"
                @click="releaseClientLink"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
                <span>שחרר</span>
              </button>
            </div>
          </div>

          <!-- Sub-Clients Section -->
          <div class="drawer-section">
            <h3 class="section-title">תת-לקוחות משויכים</h3>
            <div v-if="subClients.length" class="sub-clients-list">
              <div v-for="sc in subClients" :key="sc.makat" class="sub-client-item">
                <span class="sub-client-info"> {{ sc.makat }} — {{ sc.name || 'ללא שם' }} </span>
                <button
                  class="btn-release-sub"
                  :disabled="drawerLoading"
                  @click="releaseSubClient(sc.makat)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                  <span>שחרר</span>
                </button>
              </div>
            </div>
            <div v-else class="sub-clients-empty">אין תת-לקוחות משויכים</div>
            <div class="client-link-buttons">
              <button
                class="btn-link-client"
                :disabled="drawerLoading"
                @click="isSubClientLinkModalOpen = true"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                </svg>
                <span>שייך</span>
              </button>
            </div>
          </div>

          <!-- Access Section -->
          <div class="drawer-section">
            <h3 class="section-title">גישה</h3>
            <div class="access-toggle">
              <span class="toggle-label">סטטוס משתמש:</span>
              <span class="toggle-value" :class="{ disabled: localUser?.disabled }">
                {{ localUser?.disabled ? 'מושבת' : 'פעיל' }}
              </span>
            </div>
            <button
              class="btn-toggle-access"
              :class="{ 'btn-enable': localUser?.disabled }"
              :disabled="drawerLoading"
              @click="toggleDrawerDisabled"
            >
              {{ localUser?.disabled ? 'הפעל משתמש' : 'השבת משתמש' }}
            </button>
          </div>

          <!-- Delete Section -->
          <div class="drawer-section drawer-section-danger">
            <h3 class="section-title section-title-danger">מחיקת משתמש</h3>
            <p class="delete-warning">
              פעולה זו תמחק את המשתמש מ-Firebase Auth ותסמן את הפרופיל כנמחק (אם קיים).
            </p>
            <button class="btn-delete-user" :disabled="drawerLoading" @click="openDeleteFromDrawer">
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="3 6 5 6 21 6" />
                <path
                  d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
              <span>מחק משתמש</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Client Link Modal -->
  <ClientLinkModal
    :open="isClientLinkModalOpen"
    :user-uid="localUser?.uid || ''"
    :current-client-makat="localUser?.profile?.client_makat || null"
    :current-client-name="localUser?.profile?.client_name || null"
    @close="isClientLinkModalOpen = false"
    @linked="handleClientLinked"
  />

  <!-- Sub-Client Link Modal -->
  <ClientLinkModal
    :open="isSubClientLinkModalOpen"
    :user-uid="localUser?.uid || ''"
    mode="sub"
    :excluded-makats="subClientExcludedMakats"
    @close="isSubClientLinkModalOpen = false"
    @linked="handleSubClientLinked"
  />
</template>

<style scoped>
/* Drawer overlay & container */
.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 1000;
  display: flex;
  justify-content: flex-start;
}

.drawer {
  width: 400px;
  max-width: 90vw;
  height: 100%;
  background: var(--bg-primary);
  box-shadow: var(--shadow-xl);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--gradient-header);
  color: white;
}

.drawer-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  margin: 0;
}

.drawer-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.drawer-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-lg);
}

.user-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-lg);
}

.user-avatar-container {
  position: relative;
  flex-shrink: 0;
}

.user-avatar {
  width: 48px;
  height: 48px;
  background: var(--gradient-primary);
  border-radius: var(--border-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: white;
}

.user-avatar-img {
  width: 48px;
  height: 48px;
  border-radius: var(--border-radius-full);
  object-fit: cover;
}

.avatar-edit-btn {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 24px;
  height: 24px;
  border-radius: var(--border-radius-full);
  background: var(--gradient-primary);
  border: 2px solid var(--bg-secondary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all var(--transition-fast);
  padding: 0;
}

.avatar-edit-btn:hover {
  transform: scale(1.1);
}

.btn-remove-avatar {
  background: none;
  border: none;
  color: var(--color-error);
  font-size: var(--font-size-xs);
  cursor: pointer;
  padding: 0;
  font-family: var(--font-family);
  opacity: 0.7;
  transition: opacity var(--transition-fast);
}

.btn-remove-avatar:hover {
  opacity: 1;
  text-decoration: underline;
}

.btn-remove-avatar:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Avatar edit section */
.avatar-edit-section {
  margin-bottom: var(--spacing-lg);
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
}

.avatar-edit-actions {
  display: flex;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-sm);
}

.btn-avatar-cancel {
  flex: 1;
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-avatar-cancel:hover:not(:disabled) {
  background: var(--bg-tertiary);
}

.btn-avatar-cancel:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-avatar-save {
  flex: 1;
  padding: var(--spacing-xs) var(--spacing-sm);
  border: none;
  border-radius: var(--border-radius);
  background: var(--gradient-primary);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-avatar-save:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.btn-avatar-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.avatar-error {
  font-size: var(--font-size-xs);
  color: var(--color-error);
  margin: var(--spacing-xs) 0 0;
}

.user-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.user-email {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.user-uid {
  font-size: 11px;
  font-family: monospace;
  color: var(--text-muted);
}

/* Messages */
.msg {
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  margin-bottom: var(--spacing-md);
}

.msg-error {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.msg-success {
  background: rgba(16, 185, 129, 0.1);
  color: var(--color-success);
  border: 1px solid rgba(16, 185, 129, 0.2);
}

/* Sections */
.drawer-section {
  margin-bottom: var(--spacing-xl);
  padding-bottom: var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
}

.drawer-section:last-child {
  border-bottom: none;
}

.section-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-md) 0;
}

/* Form */
.form-group {
  margin-bottom: var(--spacing-md);
}

.form-label {
  display: block;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xs);
}

.form-hint {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  margin: 0 0 var(--spacing-xs) 0;
  font-style: italic;
  opacity: 0.8;
}

.form-input {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  transition: all var(--transition-fast);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.form-input.input-error {
  border-color: var(--color-error);
}

.form-input.input-error:focus {
  border-color: var(--color-error);
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.input-error-text {
  display: block;
  font-size: var(--font-size-xs);
  color: var(--color-error);
  margin-top: var(--spacing-xs);
}

/* Phone input with prefix */
.phone-input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  overflow: hidden;
  transition: all var(--transition-fast);
}

.phone-input-wrapper:focus-within {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.phone-prefix {
  padding: var(--spacing-sm) var(--spacing-md) var(--spacing-sm) 0;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  border-left: 1px solid var(--border-color);
  user-select: none;
  white-space: nowrap;
  direction: ltr;
  text-align: left;
}

.phone-input-with-prefix {
  border: none !important;
  box-shadow: none !important;
  flex: 1;
  min-width: 0;
}

.phone-input-with-prefix:focus {
  outline: none;
  box-shadow: none !important;
}

/* Buttons */
.btn-save {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--gradient-primary);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-save:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Client link */
.client-link-display {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-tertiary);
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
}

.client-link-label {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.client-link-value {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.client-link-value.linked {
  color: var(--color-success);
}

.client-link-value.not-linked {
  color: var(--text-tertiary);
  font-style: italic;
}

.client-link-buttons {
  display: flex;
  gap: var(--spacing-sm);
}

.btn-link-client {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  border: none;
  border-radius: var(--border-radius);
  background: var(--gradient-primary);
  color: var(--color-white);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-link-client:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.btn-link-client:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-release-client {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-release-client:hover:not(:disabled) {
  border-color: var(--color-error);
  color: var(--color-error);
  background: rgba(239, 68, 68, 0.05);
}

.btn-release-client:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Sub-Clients */
.sub-clients-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  margin-bottom: var(--spacing-md);
}

.sub-client-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
}

.sub-client-info {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-success);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btn-release-sub {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 4px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.btn-release-sub:hover:not(:disabled) {
  border-color: var(--color-error);
  color: var(--color-error);
  background: rgba(239, 68, 68, 0.05);
}

.btn-release-sub:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sub-clients-empty {
  padding: var(--spacing-sm) var(--spacing-md);
  margin-bottom: var(--spacing-md);
  background: var(--bg-tertiary);
  border: 1px dashed var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  font-style: italic;
  text-align: center;
}

/* Access */
.access-toggle {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
}

.toggle-label {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.toggle-value {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-success);
}

.toggle-value.disabled {
  color: var(--color-error);
}

.btn-toggle-access {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-error);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-toggle-access.btn-enable {
  background: var(--color-success);
}

.btn-toggle-access:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Delete section */
.drawer-section-danger {
  background: rgba(239, 68, 68, 0.03);
  margin: 0 calc(-1 * var(--spacing-lg));
  padding: var(--spacing-lg);
  border-top: 1px solid rgba(239, 68, 68, 0.1);
  border-bottom: none;
}

.section-title-danger {
  color: var(--color-error);
}

.delete-warning {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  margin: 0 0 var(--spacing-md) 0;
  line-height: 1.5;
}

.btn-delete-user {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  border: 1px solid var(--color-error);
  border-radius: var(--border-radius);
  color: var(--color-error);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-delete-user:hover:not(:disabled) {
  background: var(--color-error);
  color: white;
}

.btn-delete-user:active:not(:disabled) {
  transform: scale(0.98);
}

.btn-delete-user:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Drawer Transition */
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.3s ease;
}

.drawer-enter-active .drawer,
.drawer-leave-active .drawer {
  transition: transform 0.3s ease;
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}

.drawer-enter-from .drawer,
.drawer-leave-to .drawer {
  transform: translateX(-100%);
}

@media (max-width: 768px) {
  .drawer {
    width: 100%;
    max-width: none;
  }
}
</style>
